//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React, { ReactNode } from 'react';
import { Icon } from 'unison';

import styled from 'styled-components';

interface IOwnProps extends React.HTMLAttributes<HTMLDivElement> {
  handleClick: () => void;
  imgSrc: string;
  isDisabled?: boolean;
  isSelected?: boolean;
  overlay?: ReactNode;
  showOverlay?: boolean;
  showSelectableOverlay?: boolean;
  showBorder?: boolean;
  hoverOverlay?: ReactNode;
}

export const SelectableImage: React.FunctionComponent<IOwnProps> = (props) => {
  const {
    handleClick,
    imgSrc,
    isSelected,
    isDisabled,
    className,
    showBorder,
    overlay,
    showOverlay,
    showSelectableOverlay,
    hoverOverlay,
  } = props;
  const [isHovered, setIsHovered] = React.useState(false);

  const newOverlay = (
    <>
      {isHovered && hoverOverlay}
      {isDisabled && <DisabledOverlay />}
      {overlay}
      {showSelectableOverlay && (
        <SelectedImageIcon
          isSelected={isSelected}
          name='circle-checkmark-filled'
        />
      )}
    </>
  );

  return (
    <SelectedImageContainer
      isSelected={isSelected}
      onClick={!isDisabled ? handleClick : undefined}
      isBorderVisible={showBorder}
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      isDisabled={isDisabled}
    >
      {showOverlay && newOverlay}
      <ImagePlaceHolder src={imgSrc} />
    </SelectedImageContainer>
  );
};

SelectableImage.defaultProps = {
  handleClick: () => undefined,
  showOverlay: true,
};

const ImagePlaceHolder = styled.img`
  max-width: 100%;
  max-height: 100%;
`;

interface IContainerProps {
  isSelected: boolean;
  isBorderVisible: boolean;
  isDisabled: boolean;
}

const SelectedImageContainer = styled.div<IContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 4px solid;
  width: 100%;
  height: 100%;
  background-color: ${({ theme, isSelected, isBorderVisible }) =>
    isSelected && isBorderVisible ? theme.colors.gray0 : 'transparent'};
  border-color: ${({ isSelected, theme, isBorderVisible }) =>
    isSelected && isBorderVisible
      ? theme.colors.aliases.primary
      : 'transparent'};

  ${({ isDisabled }) =>
    !isDisabled &&
    `
    &:hover {
      cursor: pointer;
    }
  `}
`;

interface IIconProps {
  isSelected: boolean;
}
const SelectedImageIcon = styled(Icon)<IIconProps>`
  border: none;
  position: absolute;
  top: 4px;
  left: 4px;
  width: 25px;
  height: 25px;
  opacity: 1;
  border-radius: 100px;

  .cu-icon {
    width: 100%;
    height: 100%;
  }

  ${({ isSelected, theme }) => {
    if (isSelected) {
      return `
        color: ${theme.colors.aliases.primary};
        background:
          radial-gradient(
            60% 60% at center,
            ${theme.colors.white},
            ${theme.colors.white},
            ${theme.colors.aliases.primary}
          );
      `;
    }

    return `
      color: ${theme.colors.white};
      opacity: 0.6;

      &:hover {
          opacity: 0.8;
      }
    `;
  }}
`;

const DisabledOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.gray2};
  opacity: 0.6;
  z-index: 1;
`;

export default SelectableImage;
